/**
 * useKeyboard Hook
 * Manages keyboard input for games
 */

import { useEffect, useRef, useCallback } from 'react';

export const useKeyboard = (onKeyDown, onKeyUp) => {
  const keysRef = useRef({});

  const handleKeyDown = useCallback((event) => {
    const key = event.key.toLowerCase();
    keysRef.current[key] = true;
    if (onKeyDown) {
      onKeyDown(key, event);
    }
  }, [onKeyDown]);

  const handleKeyUp = useCallback((event) => {
    const key = event.key.toLowerCase();
    keysRef.current[key] = false;
    if (onKeyUp) {
      onKeyUp(key, event);
    }
  }, [onKeyUp]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  // Helper function to check if a key is currently pressed
  const isKeyPressed = useCallback((key) => {
    return keysRef.current[key.toLowerCase()] || false;
  }, []);

  // Helper function to get all pressed keys
  const getPressedKeys = useCallback(() => {
    return Object.keys(keysRef.current).filter(key => keysRef.current[key]);
  }, []);

  return {
    isKeyPressed,
    getPressedKeys,
    keysRef,
  };
};

export default useKeyboard;
